
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BranchesMap from './components/BranchesMap';

const AboutPage = () => {
  // Set page metadata using React 19 features
  useEffect(() => {
    // Set page title
    document.title = 'À propos ADF Biomédical Baraki Alger - Établissement pharmaceutique certifié Algérie';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'ADF Biomédical, établissement pharmaceutique créé en 2013 à Baraki Alger. Plus de 10 ans d\'expérience en importation, fabrication et distribution de dispositifs médicaux certifiés en Algérie.');
    }
    
    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'ADF Biomédical Baraki, établissement pharmaceutique Alger, importation dispositifs médicaux Algérie, fabrication matériel médical, distribution équipements hospitaliers');
    }
    
    // Set canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', 'https://adfbiomedical.dz/about');
    
    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'À propos ADF Biomédical - Établissement pharmaceutique Baraki Alger');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Plus de 10 ans d\'expérience en dispositifs médicaux en Algérie. Importation, fabrication et distribution.');
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://adfbiomedical.dz/about');
    }
    
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', 'website');
    }
    
    // Add structured data
    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "À propos de ADF Biomédical",
      "description": "Établissement pharmaceutique spécialisé dans les dispositifs médicaux en Algérie depuis 2013",
      "url": "https://adfbiomedical.dz/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "SARL ADF Biomédical",
        "foundingDate": "2013",
        "description": "Importation, fabrication et distribution de dispositifs médicaux en Algérie"
      }
    });
  }, []);

  const keyFigures = [
    { icon: 'ri-calendar-line', label: 'Année de création', value: '2013' },
    { icon: 'ri-money-dollar-circle-line', label: 'Capital social', value: '10 000 000 DZD' },
    { icon: 'ri-line-chart-line', label: 'Chiffre d\'affaires 2021', value: '~2 millions USD' },
  ];

  const expertise = [
    { value: '+10 ans', description: 'Plus de 10 années d\'expérience sur le marché algérien' },
    { value: '+40 ans', description: 'Expertise cumulée de l\'équipe dirigeante' },
  ];

  const activities = [
    {
      icon: 'ri-ship-line',
      title: 'Importation',
      description: 'Importation de dispositifs médicaux de haute qualité auprès de fabricants internationaux reconnus.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Fabrication',
      description: 'Fabrication locale de dispositifs médicaux à usage unique, contribuant au développement de l\'industrie pharmaceutique nationale.',
    },
    {
      icon: 'ri-truck-line',
      title: 'Distribution',
      description: 'Distribution efficace de consommables essentiels et de produits stériles à travers tout le territoire algérien.',
    },
  ];

  const qualityPillars = [
    { icon: 'ri-shield-check-line', title: 'Normes en vigueur' },
    { icon: 'ri-arrow-up-circle-line', title: 'Amélioration continue' },
    { icon: 'ri-handshake-line', title: 'Relations durables' },
    { icon: 'ri-building-2-line', title: 'Secteur public & privé' },
  ];

  return (
    <>
      <header>
        <h1 className="sr-only">
          Un établissement pharmaceutique de référence
        </h1>
      </header>
      <Navbar />
      <main>
        {/* HERO SECTION */}
        <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <p className="text-sm text-text-secondary mb-6">
                <Link to="/" className="hover:text-[#E31B23] transition-colors cursor-pointer">Accueil</Link> / À propos
              </p>
              <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
                ADF Biomédical Baraki Alger
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                Plus de 10 ans d'excellence dans le secteur médical algérien
              </p>
            </motion.div>
          </div>
        </section>

        {/* PRÉSENTATION DE L'ENTREPRISE */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto mb-16"
            >
              <p className="text-lg text-text-secondary leading-relaxed mb-6">
                <strong>A.D.F Biomedical</strong> est un établissement pharmaceutique créé en <strong>2013</strong>, constitué sous la forme d'une Société à Responsabilité Limitée (SARL). En 2021, l'entreprise a réalisé un chiffre d'affaires d'environ <strong>2 millions USD</strong>, témoignant de sa croissance continue et de sa solidité sur le marché médical algérien.
              </p>
              <p className="text-lg text-text-secondary leading-relaxed">
                L'activité principale couvre l'<strong>importation</strong>, la <strong>fabrication</strong> et la <strong>distribution</strong> de dispositifs médicaux à usage unique et de consommables essentiels, avec une spécialisation particulière dans les <strong>produits stériles</strong>.
              </p>
            </motion.div>

            {/* Cartes chiffres clés */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {keyFigures.map((figure, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-[#E31B23]"
                >
                  <div className="w-14 h-14 flex items-center justify-center mb-6 bg-[#E31B23]/10 rounded-full">
                    <i className={`${figure.icon} text-3xl text-[#E31B23]`}></i>
                  </div>
                  <p className="text-sm font-medium text-text-secondary mb-3">{figure.label}</p>
                  <p className="text-2xl font-bold text-text-primary">{figure.value}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* UNE EXPERTISE RECONNUE */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h3 className="text-4xl font-bold text-text-primary mb-4">Une expertise reconnue</h3>
              <p className="text-lg text-text-secondary">
                Des années d'expérience au service de la santé en Algérie
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {expertise.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white rounded-xl p-12 text-center shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-6xl font-bold text-[#E31B23] mb-4">{item.value}</div>
                  <p className="text-base text-text-secondary leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <p className="text-lg text-text-secondary leading-relaxed">
                ADF Biomedical s'est imposée comme un acteur fiable et reconnu dans le secteur des dispositifs médicaux, garantissant un savoir-faire exceptionnel dans le domaine médical et pharmaceutique.
              </p>
            </motion.div>
          </div>
        </section>

        {/* NOS ACTIVITÉS PRINCIPALES */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h3 className="text-4xl font-bold text-text-primary mb-4">Nos activités principales</h3>
              <p className="text-lg text-text-secondary">
                Des solutions complètes pour les établissements de santé
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {activities.map((activity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-[#E31B23]/10 rounded-full">
                    <i className={`${activity.icon} text-4xl text-[#E31B23]`}></i>
                  </div>
                  <h4 className="text-2xl font-bold text-text-primary mb-4">{activity.title}</h4>
                  <p className="text-base text-text-secondary leading-relaxed">{activity.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SPÉCIALISATION EN PRODUITS STÉRILES */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/3e6f353e8dbbec4f9360c3b978f4989c/4cba10b2db3f2cd6055a846a01034b90.jpeg"
                    alt="Dispositifs médicaux stériles"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h3 className="text-4xl font-bold text-text-primary mb-6">
                  Spécialisation en produits stériles
                </h3>
                <p className="text-lg text-text-secondary leading-relaxed mb-6">
                  ADF Biomedical possède une spécialisation particulière dans les <strong>produits stériles</strong>, indispensables au bon fonctionnement des établissements de santé.
                </p>
                <p className="text-lg text-text-secondary leading-relaxed">
                  Nos dispositifs médicaux à usage unique et consommables essentiels répondent aux normes les plus strictes en matière de stérilité et de sécurité.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CONFORMITÉ & ENGAGEMENT QUALITÉ */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto mb-16"
            >
              <div className="bg-gradient-to-r from-[#E31B23] to-[#C41820] rounded-2xl p-12 text-center shadow-2xl">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-white/20 rounded-full">
                  <i className="ri-shield-check-line text-5xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white mb-4">Conformité garantie</h4>
                <p className="text-lg text-white/90 leading-relaxed">
                  Tous nos produits stériles sont certifiés et conformes aux réglementations algériennes et internationales.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section rouge pleine largeur */}
        <section className="py-24 bg-gradient-to-r from-[#E31B23] to-[#C41820]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h3 className="text-4xl font-bold text-white mb-4">Notre engagement qualité</h3>
              <p className="text-xl text-white/90">
                Une démarche rigoureuse pour des relations durables
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {qualityPillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center hover:bg-white/20 transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-white/20 rounded-full">
                    <i className={`${pillar.icon} text-4xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-white">{pillar.title}</h4>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* NOTRE MISSION */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h3 className="text-4xl font-bold text-text-primary mb-8">Notre mission</h3>
              <p className="text-xl text-text-secondary leading-relaxed max-w-4xl mx-auto mb-12">
                À travers l'amélioration continue de ses processus et le respect des standards réglementaires, A.D.F Biomedical contribue activement au développement et à la sécurisation des services de santé en Algérie.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link
                  to="/contact"
                  className="px-10 py-4 bg-[#E31B23] text-white rounded-lg font-semibold text-lg hover:bg-[#C41820] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                >
                  Rejoignez nos partenaires de confiance
                </Link>
                <Link
                  to="/contact"
                  className="px-10 py-4 bg-white text-[#E31B23] border-2 border-[#E31B23] rounded-lg font-semibold text-lg hover:bg-[#E31B23] hover:text-white transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
                >
                  Nous contacter
                </Link>
                <Link
                  to="/solutions"
                  className="px-10 py-4 bg-white text-[#E31B23] border-2 border-[#E31B23] rounded-lg font-semibold text-lg hover:bg-[#E31B23] hover:text-white transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
                >
                  Découvrir nos solutions
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* BRANCHES MAP */}
        <BranchesMap />
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;
